import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { invalidateFacilitiesCache } from '../hooks/useFacilities';
import { toast } from 'sonner';
import { Plus, X, Building2, MapPin, ShoppingBag, ChevronDown, ChevronUp, Clock, RotateCcw } from 'lucide-react';

const SPORTS = ['Football', 'Pickleball', 'Badminton', 'Basketball', 'Tennis', 'Cricket', 'Other'];

// 16 default hourly slots (06:00 → 22:00 IST) — mirrors backend slot_config.default_slots.
function defaultSlots() {
  const slots = [];
  for (let h = 6; h < 22; h++) {
    slots.push({
      start_time: `${String(h).padStart(2, '0')}:00`,
      end_time: `${String(h + 1).padStart(2, '0')}:00`,
      price: 0,
      active: true,
    });
  }
  return slots;
}

// "HH:MM" ↔ minutes-since-midnight.
const hmToMin = (hm) => { const [h, m] = (hm || '0:0').split(':').map(Number); return h * 60 + m; };
const minToHm = (x) => `${String(Math.floor(x / 60)).padStart(2, '0')}:${String(x % 60).padStart(2, '0')}`;

// Generate back-to-back slots covering [start, end] in `lenMin` steps, each
// priced from a per-hour rate (price = rate × lenMin/60).
function generateRangeSlots(start, end, lenMin, pricePerHour) {
  const s = hmToMin(start), e = hmToMin(end);
  const len = Number(lenMin) || 60;
  if (!(e > s) || len <= 0) return [];
  const perSlot = Math.round((Number(pricePerHour) || 0) * len / 60);
  const out = [];
  for (let t = s; t + len <= e; t += len) {
    out.push({ start_time: minToHm(t), end_time: minToHm(t + len), price: perSlot, active: true });
  }
  return out;
}

// Merge generated slots into existing ones, keyed by start–end time (so re-filling
// a band updates its price rather than duplicating), then sort by start.
function upsertSlots(existing, generated) {
  const map = new Map();
  for (const s of existing) map.set(`${s.start_time}-${s.end_time}`, s);
  for (const g of generated) {
    const k = `${g.start_time}-${g.end_time}`;
    map.set(k, { ...(map.get(k) || {}), ...g });
  }
  return [...map.values()].sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''));
}

// Compact "{n} slots · ₹{min}–{max}" summary for a ground's active slots.
function slotSummary(slots) {
  const active = (slots || []).filter(s => s.active);
  if (!active.length) return null;
  const prices = active.map(s => Number(s.price) || 0);
  const min = Math.min(...prices), max = Math.max(...prices);
  const priceLabel = max === 0 ? 'no price set' : min === max ? `₹${max}` : `₹${min}–₹${max}`;
  return `${active.length} slot${active.length !== 1 ? 's' : ''} · ${priceLabel}`;
}

// ── Single slot row (within the ground editor) ─────────────────────────────
function SlotRow({ slot, onChange, onRemove }) {
  const invalid = slot.start_time && slot.end_time && slot.end_time <= slot.start_time;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, opacity: slot.active ? 1 : 0.5 }}>
      <input
        type="checkbox"
        checked={slot.active !== false}
        onChange={e => onChange({ ...slot, active: e.target.checked })}
        title={slot.active !== false ? 'Bookable — uncheck to disable' : 'Disabled'}
        style={{ flexShrink: 0 }}
      />
      <input
        type="time"
        value={slot.start_time || ''}
        onChange={e => onChange({ ...slot, start_time: e.target.value })}
        className="sb-form-input"
        style={{ width: 'auto', padding: '5px 6px', fontSize: 12, borderColor: invalid ? 'var(--bad-fg)' : undefined }}
      />
      <span style={{ color: 'var(--ink-4)', fontSize: 12 }}>–</span>
      <input
        type="time"
        value={slot.end_time || ''}
        onChange={e => onChange({ ...slot, end_time: e.target.value })}
        className="sb-form-input"
        style={{ width: 'auto', padding: '5px 6px', fontSize: 12, borderColor: invalid ? 'var(--bad-fg)' : undefined }}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: 3, background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 8, padding: '4px 8px', marginLeft: 'auto' }}>
        <span style={{ color: 'var(--ink-3)', fontSize: 12 }}>₹</span>
        <input
          type="number" min="0"
          value={slot.price || ''}
          onChange={e => onChange({ ...slot, price: parseFloat(e.target.value) || 0 })}
          placeholder="0"
          style={{ width: 60, background: 'transparent', border: 'none', outline: 'none', fontSize: 12, color: 'var(--ink)' }}
        />
      </div>
      <button onClick={onRemove} className="sb-btn sm ghost icon" style={{ color: 'var(--bad-fg)' }} title="Remove slot">
        <X size={13} />
      </button>
    </div>
  );
}

// ── Ground row editor ──────────────────────────────────────────────────────
function GroundRow({ ground, onChange, onRemove, isOnly }) {
  const [slotsOpen, setSlotsOpen] = useState(false);
  const [fill, setFill] = useState({ start: '06:00', end: '22:00', len: 60, price: '' });
  const slots = ground.slots || [];
  const summary = slotSummary(slots);

  // Quick-fill: generate hourly (or len-min) slots for a range at a per-hour rate
  // and merge them in (re-filling a band just updates its price).
  const applyFill = () => {
    if (!fill.start || !fill.end || hmToMin(fill.end) <= hmToMin(fill.start)) {
      toast.error('End time must be after start time'); return;
    }
    const gen = generateRangeSlots(fill.start, fill.end, fill.len, fill.price);
    if (!gen.length) { toast.error('No slots fit in that range'); return; }
    onChange({ ...ground, slots: upsertSlots(slots, gen) });
    setSlotsOpen(true);
    toast.success(`Added ${gen.length} slot${gen.length !== 1 ? 's' : ''}`);
  };
  const clearSlots = () => onChange({ ...ground, slots: [] });

  const updateSlot = (idx, updated) =>
    onChange({ ...ground, slots: slots.map((s, i) => i === idx ? updated : s) });
  const addSlot = () => {
    // Default a new slot to the hour after the last slot's end (or 6 AM).
    const last = slots[slots.length - 1];
    const startH = last ? Math.min(parseInt(last.end_time) || 6, 22) : 6;
    onChange({
      ...ground,
      slots: [...slots, {
        start_time: `${String(startH).padStart(2, '0')}:00`,
        end_time: `${String(Math.min(startH + 1, 23)).padStart(2, '0')}:00`,
        price: 0, active: true,
      }],
    });
    setSlotsOpen(true);
  };
  const removeSlot = (idx) => onChange({ ...ground, slots: slots.filter((_, i) => i !== idx) });
  const resetSlots = () => onChange({ ...ground, slots: defaultSlots() });

  return (
    <div style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: 'var(--surface-2)' }}>
        <input
          value={ground.name}
          onChange={e => onChange({ ...ground, name: e.target.value })}
          placeholder="Court / Ground name"
          className="sb-form-input"
          style={{ flex: 1, padding: '6px 10px', fontSize: 13 }}
        />
        <select
          value={ground.sport}
          onChange={e => onChange({ ...ground, sport: e.target.value })}
          className="sb-form-input"
          style={{ width: 'auto', padding: '6px 10px', fontSize: 13 }}
        >
          {SPORTS.map(s => <option key={s}>{s}</option>)}
        </select>
        <button
          type="button"
          onClick={() => setSlotsOpen(o => !o)}
          title="Edit bookable slots"
          className="sb-btn sm ghost"
          style={summary ? { color: 'var(--accent)', background: 'color-mix(in oklch, var(--accent) 10%, transparent)' } : {}}
        >
          <Clock size={12} /> {slots.filter(s => s.active).length} {slotsOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        </button>
        <button
          onClick={onRemove}
          disabled={isOnly}
          className="sb-btn sm ghost icon"
          style={{ color: 'var(--bad-fg)' }}
          title="Remove court"
        >
          <X size={14} />
        </button>
      </div>

      {slotsOpen && (
        <div style={{ padding: '12px 14px', borderTop: '1px solid var(--line-soft)' }}>
          {/* Quick fill — generate slots for a time range at a per-hour rate */}
          <div style={{ background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 8, padding: '10px 12px', marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 8 }}>
              Quick fill — generate slots for a time range
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', fontSize: 12, color: 'var(--ink-3)' }}>
              <input type="time" value={fill.start} onChange={e => setFill(f => ({ ...f, start: e.target.value }))}
                className="sb-form-input" style={{ width: 'auto', padding: '5px 6px', fontSize: 12 }} />
              <span>to</span>
              <input type="time" value={fill.end} onChange={e => setFill(f => ({ ...f, end: e.target.value }))}
                className="sb-form-input" style={{ width: 'auto', padding: '5px 6px', fontSize: 12 }} />
              <span style={{ marginLeft: 4 }}>at ₹</span>
              <input type="number" min="0" value={fill.price} placeholder="0"
                onChange={e => setFill(f => ({ ...f, price: e.target.value }))}
                className="sb-form-input" style={{ width: 64, padding: '5px 8px', fontSize: 12 }} />
              <span>/hr</span>
              <span style={{ marginLeft: 4 }}>every</span>
              <select value={fill.len} onChange={e => setFill(f => ({ ...f, len: parseInt(e.target.value) }))}
                className="sb-form-input" style={{ width: 'auto', padding: '5px 6px', fontSize: 12 }}>
                <option value={30}>30 min</option>
                <option value={60}>1 hour</option>
                <option value={90}>90 min</option>
                <option value={120}>2 hours</option>
              </select>
              <button type="button" onClick={applyFill} className="sb-btn sm brand" style={{ marginLeft: 'auto', fontSize: 12 }}>
                <Plus size={12} /> Fill
              </button>
            </div>
            <p style={{ fontSize: 10.5, color: 'var(--ink-4)', margin: '8px 0 0' }}>
              Tip: fill multiple ranges for different price bands (e.g. mornings cheaper than evenings). Re-filling a range updates its price.
            </p>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <p style={{ fontSize: 11, color: 'var(--ink-3)', margin: 0 }}>
              Bookable slots — start, end & price (₹). Uncheck to disable without deleting.
            </p>
            <div style={{ display: 'flex', gap: 6 }}>
              {slots.length > 0 && (
                <button type="button" onClick={clearSlots} className="sb-btn sm ghost" style={{ fontSize: 11, color: 'var(--bad-fg)' }} title="Remove all slots">
                  <X size={11} /> Clear
                </button>
              )}
              <button type="button" onClick={resetSlots} className="sb-btn sm ghost" style={{ fontSize: 11 }} title="Reset to 16 hourly slots">
                <RotateCcw size={11} /> Default
              </button>
            </div>
          </div>
          {slots.length === 0 ? (
            <p style={{ fontSize: 12, color: 'var(--ink-4)', fontStyle: 'italic', marginBottom: 10 }}>
              No slots — this court won't be bookable until you add some.
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
              {slots.map((s, i) => (
                <SlotRow key={i} slot={s} onChange={u => updateSlot(i, u)} onRemove={() => removeSlot(i)} />
              ))}
            </div>
          )}
          <button type="button" onClick={addSlot} className="sb-btn sm ghost" style={{ color: 'var(--brand)' }}>
            <Plus size={12} /> Add slot
          </button>
        </div>
      )}
    </div>
  );
}

// ── Rental item row editor ─────────────────────────────────────────────────
function RentalItemRow({ item, onChange, onRemove }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <input
        value={item.name}
        onChange={e => onChange({ ...item, name: e.target.value })}
        placeholder="e.g. Football Boots, Pickleball Paddle"
        className="sb-form-input"
        style={{ flex: 1, padding: '6px 10px', fontSize: 13 }}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 8, padding: '6px 10px' }} title="Stock qty">
        <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>Qty</span>
        <input
          type="number" min="1"
          value={item.quantity ?? 1}
          onChange={e => onChange({ ...item, quantity: parseInt(e.target.value) || 1 })}
          style={{ width: 36, background: 'transparent', border: 'none', outline: 'none', fontSize: 13, color: 'var(--ink)', textAlign: 'center' }}
        />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 8, padding: '6px 10px' }}>
        <span style={{ color: 'var(--ink-3)', fontSize: 13 }}>₹</span>
        <input
          type="number" min="0"
          value={item.price_per_session}
          onChange={e => onChange({ ...item, price_per_session: parseFloat(e.target.value) || 0 })}
          placeholder="0"
          style={{ width: 56, background: 'transparent', border: 'none', outline: 'none', fontSize: 13, color: 'var(--ink)' }}
        />
        <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>/session</span>
      </div>
      <button onClick={onRemove} className="sb-btn sm ghost icon" style={{ color: 'var(--bad-fg)' }} title="Remove">
        <X size={14} />
      </button>
    </div>
  );
}

// ── Facility modal ─────────────────────────────────────────────────────────
function FacilityModal({ existing, api, onClose, onSaved }) {
  const isEdit = !!existing;
  const [name, setName] = useState(existing?.name || '');
  const [grounds, setGrounds] = useState(
    existing?.grounds?.length
      ? existing.grounds.map(g => ({ ...g, slots: g.slots?.length ? g.slots : defaultSlots() }))
      : [{ id: 'court-1', name: '', sport: 'Football', slots: defaultSlots() }]
  );
  const [rentalItems, setRentalItems] = useState(existing?.rental_items || []);
  const [saving, setSaving] = useState(false);

  const addGround = () =>
    setGrounds(prev => [...prev, { id: `court-${prev.length + 1}`, name: '', sport: prev[0]?.sport || 'Football', slots: defaultSlots(), _isNew: true }]);

  const updateGround = (idx, updated) =>
    setGrounds(prev => prev.map((g, i) => {
      if (i !== idx) return g;
      if (g._isNew && updated.name) {
        const newId = updated.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') || g.id;
        return { ...updated, id: newId, _isNew: true };
      }
      return { ...updated, id: g.id };
    }));

  const removeGround = (idx) => setGrounds(prev => prev.filter((_, i) => i !== idx));
  const addRentalItem = () =>
    setRentalItems(prev => [...prev, { item_id: `item-${Date.now()}`, name: '', price_per_session: 0, quantity: 1 }]);
  const updateRentalItem = (idx, updated) =>
    setRentalItems(prev => prev.map((r, i) => i === idx ? updated : r));
  const removeRentalItem = (idx) =>
    setRentalItems(prev => prev.filter((_, i) => i !== idx));

  const handleSave = async () => {
    if (!name.trim()) { toast.error('Enter a facility name'); return; }
    // Declarative slot list: send start/end/price/active; backend derives
    // slot_code + label from the times and orders by position.
    const validGrounds = grounds.filter(g => g.name.trim()).map(({ _isNew, slots, ...g }) => ({
      ...g,
      slots: (slots || [])
        .filter(s => s.start_time && s.end_time && s.end_time > s.start_time)
        .map(s => ({
          start_time: s.start_time,
          end_time: s.end_time,
          price: Number(s.price) || 0,
          active: s.active !== false,
        })),
    }));
    if (!validGrounds.length) { toast.error('Add at least one court / ground'); return; }
    if (validGrounds.some(g => g.slots.length === 0)) {
      toast.error('Each court needs at least one valid slot (end after start)');
      return;
    }
    const validRentals = rentalItems.filter(r => r.name.trim());
    setSaving(true);
    try {
      const body = { name: name.trim(), grounds: validGrounds, rental_items: validRentals };
      if (isEdit) {
        await api.patch(`/api/facilities/${existing.location_id}`, body);
        toast.success('Facility updated');
      } else {
        await api.post('/api/facilities/', body);
        toast.success('Facility created');
      }
      invalidateFacilitiesCache();
      onSaved();
      onClose();
    } catch {
      toast.error('Failed to save facility');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="sb-overlay" onClick={onClose} />
      <div className="sb-modal" style={{ width: 560 }}>
        <div className="sb-modal-head" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div className="sb-modal-title">{isEdit ? 'Edit Facility' : 'New Facility'}</div>
            <div className="sb-modal-sub">{isEdit ? `Editing ${existing.name}` : 'Set up courts, sports, and rental catalogue'}</div>
          </div>
          <button onClick={onClose} className="sb-btn ghost icon"><X size={16} /></button>
        </div>
        <div className="sb-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Live preview */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 10 }}>
            <div className="sb-fac-logo">{(name.trim().slice(0, 2) || 'SB').toUpperCase()}</div>
            <div style={{ minWidth: 0 }}>
              <div className="sb-fac-name">{name.trim() || 'New facility'}</div>
              <div className="sb-fac-meta">
                {grounds.length} court{grounds.length !== 1 ? 's' : ''} · {rentalItems.length} rental{rentalItems.length !== 1 ? 's' : ''}
              </div>
            </div>
            <span className="sb-card-lbl" style={{ marginLeft: 'auto' }}>Preview</span>
          </div>

          {/* Name */}
          <div className="sb-field">
            <label>Facility Name</label>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. West End Futsal Arena"
              className="sb-form-input"
            />
          </div>

          {/* Courts */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <span className="sb-form-label" style={{ marginBottom: 0 }}>Courts / Grounds</span>
              <button onClick={addGround} className="sb-btn sm ghost" style={{ color: 'var(--brand)' }}>
                <Plus size={12} /> Add court
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {grounds.map((g, i) => (
                <GroundRow
                  key={i}
                  ground={g}
                  onChange={updated => updateGround(i, updated)}
                  onRemove={() => removeGround(i)}
                  isOnly={grounds.length === 1}
                />
              ))}
            </div>
          </div>

          {/* Rentals */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <span className="sb-form-label" style={{ marginBottom: 0 }}>
                Equipment Rentals <span style={{ color: 'var(--ink-4)', fontWeight: 400 }}>(optional)</span>
              </span>
              <button onClick={addRentalItem} className="sb-btn sm ghost" style={{ color: 'var(--brand)' }}>
                <Plus size={12} /> Add item
              </button>
            </div>
            {rentalItems.length === 0 ? (
              <p style={{ fontSize: 12, color: 'var(--ink-4)', fontStyle: 'italic' }}>No rental items yet. Add boots, paddles, etc.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {rentalItems.map((r, i) => (
                  <RentalItemRow
                    key={i}
                    item={r}
                    onChange={updated => updateRentalItem(i, updated)}
                    onRemove={() => removeRentalItem(i)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="sb-modal-foot">
          <button onClick={onClose} className="sb-btn">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="sb-btn primary">
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Facility'}
          </button>
        </div>
      </div>
    </>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────
export default function AdminFacilities() {
  const { api } = useAuth();
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/api/facilities/')
      .then(r => setFacilities(r.data))
      .catch(() => toast.error('Failed to load facilities'))
      .finally(() => setLoading(false));
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (f) => {
    setDeletingId(f.location_id);
    try {
      await api.delete(`/api/facilities/${f.location_id}`);
      toast.success(`${f.name} deleted`);
      invalidateFacilitiesCache();
      setConfirmDelete(null);
      load();
    } catch {
      toast.error('Failed to delete facility');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div>
      <div className="sb-page-head">
        <div>
          <div className="sb-crumb">Configure</div>
          <h1>Facilities</h1>
          <div className="sb-sub">Locations, courts, and rentable equipment. Each court sets its own bookable slots and prices.</div>
        </div>
        <div className="sb-page-actions">
          <button onClick={() => setModal('new')} className="sb-btn primary">
            <Plus size={13} /> New facility
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[1, 2].map(i => (
            <div key={i} className="sb-card" style={{ padding: 20, opacity: 0.5 }}>
              <div style={{ height: 14, background: 'var(--line)', borderRadius: 6, width: 160, marginBottom: 10 }} />
              <div style={{ height: 11, background: 'var(--line-soft)', borderRadius: 6, width: 240 }} />
            </div>
          ))}
        </div>
      ) : facilities.length === 0 ? (
        <div className="sb-card" style={{ padding: '64px 24px', textAlign: 'center' }}>
          <Building2 size={36} style={{ color: 'var(--ink-4)', margin: '0 auto 12px' }} />
          <p style={{ color: 'var(--ink-2)', marginBottom: 12 }}>No facilities yet</p>
          <button onClick={() => setModal('new')} className="sb-btn brand">
            <Plus size={13} /> Create your first facility
          </button>
        </div>
      ) : (
        <div className="sb-fac-grid">
          {facilities.map(f => (
            <div key={f.location_id} className="sb-card sb-fac-card">
              {/* Card header */}
              <div className="sb-fac-head">
                <div className="sb-fac-logo">
                  {f.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="sb-fac-name">{f.name}</div>
                  <div className="sb-fac-meta">
                    #{f.location_id} · {f.grounds?.length || 0} court{f.grounds?.length !== 1 ? 's' : ''}
                    {f.rental_items?.length > 0 && ` · ${f.rental_items.length} rental item${f.rental_items.length !== 1 ? 's' : ''}`}
                  </div>
                </div>
                {/* Actions */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                  <button onClick={() => setModal(f)} className="sb-btn sm">Edit</button>
                  {confirmDelete === f.location_id ? (
                    <>
                      <button onClick={() => setConfirmDelete(null)} className="sb-btn sm ghost">Cancel</button>
                      <button
                        onClick={() => handleDelete(f)}
                        disabled={deletingId === f.location_id}
                        className="sb-btn sm danger"
                      >
                        {deletingId === f.location_id ? 'Deleting…' : 'Confirm'}
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => setConfirmDelete(f.location_id)}
                      className="sb-btn sm ghost"
                      style={{ color: 'var(--bad-fg)' }}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>

              {/* Courts */}
              <div>
                {f.grounds?.map((g) => {
                  const summary = slotSummary(g.slots);
                  return (
                    <div key={g.id} className="sb-court-row">
                      <div>
                        <div className="sb-court-name">
                          <MapPin size={11} style={{ color: 'var(--brand)', display: 'inline', marginRight: 5, verticalAlign: 'middle' }} />
                          {g.name}
                        </div>
                        <div className="sb-court-sport">{g.sport}</div>
                      </div>
                      {summary && (
                        <span className="sb-badge plain" style={{ fontSize: 10, padding: '2px 7px', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <Clock size={9} /> {summary}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Rental items */}
              {f.rental_items?.length > 0 && (
                <div style={{ padding: '12px 20px', borderTop: '1px solid var(--line-soft)' }}>
                  <div className="sb-card-lbl" style={{ marginBottom: 8 }}>
                    <ShoppingBag size={10} style={{ display: 'inline', marginRight: 4, verticalAlign: 'middle' }} />
                    Equipment Rentals
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {f.rental_items.map(r => (
                      <div
                        key={r.item_id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          background: 'color-mix(in oklch, var(--accent) 8%, transparent)',
                          border: '1px solid color-mix(in oklch, var(--accent) 20%, transparent)',
                          borderRadius: 8,
                          padding: '5px 10px',
                          fontSize: 12,
                        }}
                      >
                        <span style={{ fontWeight: 500 }}>{r.name}</span>
                        <span style={{ color: 'var(--ink-3)' }}>×{r.quantity ?? 1}</span>
                        <span style={{ color: 'var(--accent)', fontFamily: 'var(--f-mono)' }}>₹{r.price_per_session}/session</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {modal && (
        <FacilityModal
          existing={modal === 'new' ? null : modal}
          api={api}
          onClose={() => setModal(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}
